import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Button, Spinner, Modal } from "react-bootstrap";
import { FaPaw, FaPlus } from "react-icons/fa";
import { FiEdit, FiCalendar, FiSearch, FiUsers } from "react-icons/fi";
import axios from "axios";
import PetRecordsPanel from "./PetRecordsPanel";

const blankPet = {
    name: '',
    breed_id: '',
    age: '',
    color: '',
    male: '',
    is_dead: false,
    birth_date: '',
    death_date: '',
    weight: '',
};

const PetsTab = ({
    pets,
    breeds,
    customerId,
    loading,
    highlightPetId,
    totalPets,
    isFilteredToPet,
    onShowAllPets,
    onPetsUpdate,
    onShowSuccess,
    onShowError,
}) => {
    const highlightRef = useRef(null);
    const [editingPet, setEditingPet] = useState(null);
    const [savingPet, setSavingPet] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingPet, setCreatingPet] = useState(false);
    const [selectedPetId, setSelectedPetId] = useState(null);

    const [petFormData, setPetFormData] = useState(blankPet);
    const [newPetFormData, setNewPetFormData] = useState(blankPet);

    // One pet is always the subject of the panel, so a tab is picked for the
    // user: the pet they searched for when there is one, otherwise the first.
    useEffect(() => {
        if (pets.length === 0) {
            setSelectedPetId(null);
            return;
        }

        const searched = pets.find((pet) => String(pet.id) === String(highlightPetId));

        setSelectedPetId((current) => {
            if (current !== null && pets.some((pet) => String(pet.id) === String(current))) {
                return current;
            }

            return searched ? searched.id : pets[0].id;
        });
    }, [pets, highlightPetId]);

    // The searched pet's tab can sit off the end of a long strip, so it is
    // brought into view rather than left for the user to find.
    useEffect(() => {
        if (highlightPetId && highlightRef.current) {
            highlightRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }
    }, [highlightPetId, pets.length]);

    const handleOpenPetEdit = (pet) => {
        setEditingPet(pet);
        setPetFormData({
            name: pet.name || '',
            breed_id: pet.breed_id || '',
            age: pet.age || '',
            color: pet.color || '',
            male: pet.male !== undefined && pet.male !== null ? pet.male.toString() : '',
            is_dead: pet.is_dead === 1 || pet.is_dead === true || pet.is_dead === "1",
            birth_date: pet.birth_date || '',
            death_date: pet.death_date || '',
            weight: pet.weight || '',
        });
    };

    const handleClosePetEdit = () => {
        setEditingPet(null);
        setPetFormData(blankPet);
    };

    const handlePetFieldChange = (e) => {
        const { name, value, type, checked } = e.target;

        setPetFormData((prev) => ({
            ...prev,
            [name]:
                name === "is_dead"
                    ? value === "1"
                    : type === "checkbox"
                        ? checked
                        : value,
        }));
    };

    const handleNewPetFieldChange = (e) => {
        const { name, value, type, checked } = e.target;

        setNewPetFormData((prev) => ({
            ...prev,
            [name]:
                name === "is_dead"
                    ? value === "1"
                    : type === "checkbox"
                        ? checked
                        : value,
        }));
    };

    const reportError = (err, fallback) => {
        console.error(fallback, err);

        if (err.response?.data?.errors) {
            const errors = err.response.data.errors;
            onShowError(Object.values(errors).flat().join(', '));
        } else {
            onShowError(err.response?.data?.message || fallback);
        }
    };

    const handleUpdatePet = async () => {
        if (!editingPet) return;

        try {
            setSavingPet(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = {
                name: petFormData.name,
                breed_id: petFormData.breed_id,
                age: petFormData.age,
                color: petFormData.color,
                male: petFormData.male === '1' || petFormData.male === true,
                is_dead: petFormData.is_dead,
                birth_date: petFormData.birth_date,
                death_date: petFormData.death_date,
                weight: petFormData.weight,
                customer_id: customerId
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets-update/${editingPet.id}`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Pet updated successfully");
                handleClosePetEdit();
                onPetsUpdate();
            } else {
                onShowError(response.data.message || "Failed to update pet");
            }
        } catch (err) {
            reportError(err, "Failed to update pet");
        } finally {
            setSavingPet(false);
        }
    };

    const handleCreatePet = async () => {
        try {
            setCreatingPet(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = {
                name: newPetFormData.name,
                breed_id: newPetFormData.breed_id,
                age: newPetFormData.age,
                color: newPetFormData.color,
                male: newPetFormData.male === '1' || newPetFormData.male === true,
                is_dead: newPetFormData.is_dead,
                birth_date: newPetFormData.birth_date,
                death_date: newPetFormData.death_date,
                weight: newPetFormData.weight,
                customer_id: customerId
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Pet created successfully");
                setShowCreateModal(false);
                setNewPetFormData(blankPet);
                onPetsUpdate();
            } else {
                onShowError(response.data.message || "Failed to create pet");
            }
        } catch (err) {
            reportError(err, "Failed to create pet");
        } finally {
            setCreatingPet(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const formatGender = (male) => {
        if (male === 1 || male === true) return 'Male';
        if (male === 0 || male === false) return 'Female';
        return 'N/A';
    };

    const formatBreed = (breedId) => {
        const findBreed = (breedsList, id) => {
            for (let breed of breedsList) {
                if (breed.id === id) return breed.name;
                if (breed.child_breeds && breed.child_breeds.length > 0) {
                    const found = findBreed(breed.child_breeds, id);
                    if (found) return found;
                }
            }
            return null;
        };
        return findBreed(breeds, breedId) || 'N/A';
    };

    const renderBreedsOptions = (breedsList, level = 0) => {
        let options = [];
        breedsList.forEach(breed => {
            options.push(
                <option key={breed.id} value={breed.id} style={{ paddingLeft: `${level * 20}px` }}>
                    {' '.repeat(level * 4)}{breed.name}
                </option>
            );
            if (breed.child_breeds && breed.child_breeds.length > 0) {
                options.push(...renderBreedsOptions(breed.child_breeds, level + 1));
            }
        });
        return options;
    };

    const formatVisitDate = (value) => {
        if (!value) return null;

        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(new Date(value));
    };

    const petFields = (pet) => [
        { label: 'Pet Name', value: pet.name },
        { label: 'Breed', value: pet.breed_name || formatBreed(pet.breed_id) },
        { label: 'Gender', value: formatGender(pet.male) },
        { label: 'Color', value: pet.color },
        { label: 'Age (years)', value: pet.age },
        { label: 'Weight (kg)', value: pet.weight },
        { label: 'Birth Date', value: formatDate(pet.birth_date) },
        { label: 'Status', value: pet.is_dead ? 'Deceased' : 'Alive' },
        ...(pet.is_dead ? [{ label: 'Death Date', value: formatDate(pet.death_date) }] : []),
    ];

    const petFormFields = (formData, onChange) => (
        <>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                            Pet Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={onChange}
                            placeholder="Enter pet name"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Breed</Form.Label>
                        <Form.Select name="breed_id" value={formData.breed_id} onChange={onChange}>
                            <option value="">Select Breed</option>
                            {renderBreedsOptions(breeds)}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Gender</Form.Label>
                        <Form.Select name="male" value={formData.male} onChange={onChange}>
                            <option value="">Select Gender</option>
                            <option value="1">Male</option>
                            <option value="0">Female</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Color</Form.Label>
                        <Form.Control
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={onChange}
                            placeholder="Enter color"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Age (years)</Form.Label>
                        <Form.Control
                            type="text"
                            name="age"
                            value={formData.age}
                            onChange={onChange}
                            placeholder="Enter age"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Weight (kg)</Form.Label>
                        <Form.Control
                            type="text"
                            name="weight"
                            value={formData.weight}
                            onChange={onChange}
                            placeholder="Enter weight"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Birth Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="birth_date"
                            value={formData.birth_date || ''}
                            onChange={onChange}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Status</Form.Label>
                        <Form.Select
                            name="is_dead"
                            value={formData.is_dead ? "1" : "0"}
                            onChange={onChange}
                        >
                            <option value="0">Alive</option>
                            <option value="1">Deceased</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {formData.is_dead && (
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Death Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="death_date"
                                value={formData.death_date || ""}
                                onChange={onChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
        </>
    );

    if (loading) {
        return (
            <div className="cp-empty">
                <Spinner animation="border" variant="primary" />
                <p>Loading pets...</p>
            </div>
        );
    }

    const hiddenPets = Math.max(0, (totalPets || pets.length) - pets.length);
    const selectedPet = pets.find((pet) => String(pet.id) === String(selectedPetId)) || null;

    return (
        <div>
            <div className="cp-section-head">
                <div>
                    <span className="cp-eyebrow">registered pets</span>
                    <h5 className="cp-section-title">
                        {pets.length} {pets.length === 1 ? 'pet' : 'pets'}
                        {isFilteredToPet && hiddenPets > 0 && (
                            <span className="cp-record-sub">
                                Showing the searched pet only
                            </span>
                        )}
                    </h5>
                </div>

                <div className="cp-tools">
                    {isFilteredToPet && hiddenPets > 0 && (
                        <button
                            type="button"
                            className="cp-ghost-btn"
                            onClick={onShowAllPets}
                        >
                            <FiUsers size={13} /> Show all {totalPets} pets
                        </button>
                    )}
                    <button
                        type="button"
                        className="cp-ghost-btn is-solid"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FaPlus size={11} /> Add New Pet
                    </button>
                </div>
            </div>

            {pets.length === 0 ? (
                <div className="cp-empty">
                    <FaPaw size={38} />
                    <p>No pets found for this customer.</p>
                </div>
            ) : (
                <>
                    <nav className="cp-pet-tabs">
                        {pets.map((pet) => {
                            const isHighlighted =
                                highlightPetId && String(pet.id) === String(highlightPetId);
                            const isSelected = String(selectedPetId) === String(pet.id);

                            return (
                                <button
                                    key={pet.id}
                                    type="button"
                                    ref={isHighlighted ? highlightRef : null}
                                    className={`cp-pet-tab ${isSelected ? "active" : ""} ${
                                        pet.is_dead ? "is-gone" : ""
                                    }`}
                                    onClick={() => setSelectedPetId(pet.id)}
                                >
                                    <span className="cp-pet-tab-avatar">
                                        <FaPaw size={15} />
                                    </span>
                                    <span className="cp-pet-tab-body">
                                        <span className="cp-pet-tab-name">
                                            {pet.name || "Unnamed"}
                                            {isHighlighted && <FiSearch size={10} />}
                                        </span>
                                        <span className="cp-pet-tab-sub">
                                            {[
                                                pet.breed_name,
                                                formatGender(pet.male),
                                                pet.is_dead ? "Deceased" : null,
                                            ]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </span>
                                    </span>
                                    <span className="cp-pet-tab-count">
                                        {pet.appointments_count || 0}
                                    </span>
                                </button>
                            );
                        })}
                    </nav>

                    {selectedPet && (
                        <div
                            className={`cp-pet-card ${
                                highlightPetId && String(selectedPet.id) === String(highlightPetId)
                                    ? "is-highlight"
                                    : ""
                            }`}
                        >
                            <div className="cp-pet-head">
                                <div className="cp-pet-ident">
                                    <div className="cp-pet-avatar">
                                        <FaPaw size={22} />
                                    </div>
                                    <div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <h4 className="cp-pet-name">{selectedPet.name}</h4>
                                            {highlightPetId &&
                                                String(selectedPet.id) === String(highlightPetId) && (
                                                    <span className="cp-tag is-blue">
                                                        <FiSearch size={10} /> Searched pet
                                                    </span>
                                                )}
                                            {selectedPet.is_dead ? (
                                                <span className="cp-tag is-gray">Deceased</span>
                                            ) : null}
                                        </div>
                                        <div className="cp-pet-meta">
                                            {selectedPet.breed_name && <span>{selectedPet.breed_name}</span>}
                                            <span>{selectedPet.appointments_count || 0} appointments</span>
                                            {formatVisitDate(selectedPet.last_visit) && (
                                                <span>
                                                    <FiCalendar size={11} /> Last visit{" "}
                                                    {formatVisitDate(selectedPet.last_visit)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="cp-ghost-btn"
                                    onClick={() => handleOpenPetEdit(selectedPet)}
                                >
                                    <FiEdit size={13} /> Edit Pet
                                </button>
                            </div>

                            <div className="cp-record-grid cp-pet-fields">
                                {petFields(selectedPet).map((field) => (
                                    <div key={field.label}>
                                        <span>{field.label}</span>
                                        <strong>{field.value || 'N/A'}</strong>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 pt-3 border-top">
                                <PetRecordsPanel petId={selectedPet.id} petName={selectedPet.name} />
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Pet Modal */}
            <Modal show={Boolean(editingPet)} onHide={handleClosePetEdit} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit {editingPet?.name || 'Pet'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{petFormFields(petFormData, handlePetFieldChange)}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClosePetEdit} disabled={savingPet}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdatePet} disabled={savingPet}>
                        {savingPet ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Pet Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Pet</Modal.Title>
                </Modal.Header>
                <Modal.Body>{petFormFields(newPetFormData, handleNewPetFieldChange)}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreatePet} disabled={creatingPet}>
                        {creatingPet ? 'Creating...' : 'Create Pet'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PetsTab;
